import type { AgentRecord } from "@/services/studio/types";

export type TargetKind = "org" | "apex" | "object" | "flow" | "permission" | "metadata" | "user";

export interface TargetPolicy {
  kind: TargetKind;
  title: string;
  status: string;
  required: boolean;
  runAll?: boolean;
  metadataTypes?: string[];
  metadataType?: string;
  targetLabel?: string;
  placeholder?: string;
  emptyHint?: string;
  help: string;
  // Org-scoped agents that still need a NAMED entity (a user, a pricebook, …)
  // typed into the Instruction box — there's no metadata picker for these, so the
  // form must prompt for it instead of saying "no target needed".
  needsInput?: boolean;
  inputHint?: string;
}

export const ORG_TARGET_POLICY: TargetPolicy = {
  kind: "org",
  title: "Org Scope",
  status: "inherited",
  required: false,
  help: "Uses the org and user from your login. No target is needed for this run.",
};

export function scopeString(scope: Record<string, unknown> | undefined, key: string, fallback = ""): string {
  const v = scope?.[key];
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return fallback;
}

export function scopeBool(scope: Record<string, unknown> | undefined, key: string): boolean {
  const v = scope?.[key];
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true";
  return false;
}

export function scopeStringArray(scope: Record<string, unknown> | undefined, key: string): string[] {
  const v = scope?.[key];
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === "string" && v) return [v];
  return [];
}

export function hasTool(agent: AgentRecord, ...tools: string[]): boolean {
  const set = new Set(agent.tools || []);
  return tools.some((t) => set.has(t));
}

export function agentText(agent: AgentRecord): string {
  return `${agent.name} ${agent.slug} ${agent.goal} ${agent.description}`.toLowerCase();
}

export function deriveTargetPolicy(agent: AgentRecord): TargetPolicy {
  const declaredTypes = scopeStringArray(agent.scope, "metadata_types").length
    ? scopeStringArray(agent.scope, "metadata_types")
    : scopeStringArray(agent.scope, "metadataTypes");
  const mode = scopeString(agent.scope, "mode", "org");
  const required = scopeBool(agent.scope, "target_required") || scopeBool(agent.scope, "targetRequired");
  const runAll = scopeBool(agent.scope, "run_all") || scopeBool(agent.scope, "runAll");

  if (declaredTypes.includes("ApexClass") || declaredTypes.includes("ApexTrigger")) {
    return { kind: "apex", title: required ? "Apex Target" : "Apex Scope", status: required ? "required" : runAll ? "run-all-or-target" : "optional", required, runAll, metadataTypes: declaredTypes.length ? declaredTypes : ["ApexClass", "ApexTrigger"], targetLabel: "Apex metadata", placeholder: "Select Apex class or trigger", emptyHint: "No Apex metadata found.", help: "Metadata types and files are loaded from the connected org." };
  }
  if (declaredTypes.length === 1 && declaredTypes[0] === "CustomObject") {
    return { kind: "object", title: required ? "Object Target" : "Object Scope", status: required ? "required" : runAll ? "run-all-or-object" : "optional", required, runAll, metadataTypes: declaredTypes, metadataType: "CustomObject", targetLabel: "Salesforce object", placeholder: "Select object", emptyHint: "No object found.", help: "Objects and fields are loaded from the connected org." };
  }
  if (declaredTypes.length === 1 && declaredTypes[0] === "Flow") {
    return { kind: "flow", title: required ? "Flow Target" : "Flow Scope", status: required ? "required" : runAll ? "run-all-or-flow" : "optional", required, runAll, metadataTypes: declaredTypes, metadataType: "Flow", targetLabel: "Flow", placeholder: "Select flow", emptyHint: "No flow found.", help: "Flows are loaded from the connected org." };
  }
  if (declaredTypes.length === 1 && declaredTypes[0] === "PermissionSet") {
    return { kind: "permission", title: required ? "Permission Target" : "Permission Scope", status: required ? "required" : runAll ? "run-all-or-permission" : "optional", required, runAll, metadataTypes: declaredTypes, metadataType: "PermissionSet", targetLabel: "Permission set", placeholder: "Select permission set", emptyHint: "No permission set found.", help: "Permission sets are loaded from the connected org." };
  }
  if (mode === "metadata" || required || declaredTypes.length > 1) {
    const lockedType = declaredTypes.length === 1 ? declaredTypes[0] : undefined;
    const typeLabel = lockedType
      ? lockedType.replace(/([A-Z])/g, " $1").trim()
      : "Metadata item";
    return {
      kind: "metadata",
      title: required ? "Metadata Target" : "Metadata Scope",
      status: required ? "required" : runAll ? "run-all-or-target" : "optional",
      required, runAll,
      metadataTypes: declaredTypes,
      metadataType: lockedType,
      targetLabel: typeLabel,
      placeholder: lockedType ? `Select ${typeLabel}` : "Select metadata",
      emptyHint: lockedType ? `No ${typeLabel} found.` : "No metadata found.",
      help: "Choose a metadata type, then the corresponding file.",
    };
  }

  const text = agentText(agent);
  const broadOrg = /org health|org 360|full metadata|release readiness|deployment readiness|deployment prep|metadata search|metadata export|security hardening|test coverage|test gap|performance investigation/.test(text);

  if (!broadOrg && (hasTool(agent, "get_object_fields", "graph_object_explorer", "graph_object_usage", "graph_triggers_on") || /\bobject impact\b|\bfield cleanup\b|\bdata model\b|\bobject cleanup\b/.test(text))) {
    return { kind: "object", title: "Object Target", status: "object", required: true, metadataTypes: ["CustomObject"], metadataType: "CustomObject", targetLabel: "Salesforce object", placeholder: "Select object", emptyHint: "No object found.", help: "This agent needs one object to inspect fields, records, and triggers." };
  }
  if (!broadOrg && (hasTool(agent, "get_class_source", "get_class_detail", "graph_calls", "graph_data_access", "search_similar_classes", "run_apex_test") || /\bapex\b|code review|refactor|test generator/.test(text))) {
    return { kind: "apex", title: "Apex Target", status: "class-or-trigger", required: true, metadataTypes: ["ApexClass", "ApexTrigger"], targetLabel: "Apex class or trigger", placeholder: "Select an Apex class or trigger", emptyHint: "No Apex class or trigger found.", help: "This agent works on Apex code and needs a concrete class or trigger." };
  }
  if (!broadOrg && (hasTool(agent, "list_flows") || /\bflow\b/.test(text))) {
    return { kind: "flow", title: "Flow Target", status: "flow", required: true, metadataTypes: ["Flow"], metadataType: "Flow", targetLabel: "Flow", placeholder: "Select flow", emptyHint: "No flow found.", help: "This agent is Flow-specific." };
  }
  if (!broadOrg && (hasTool(agent, "list_perm_sets") || /permission set fix|access fix/.test(text))) {
    return { kind: "permission", title: "Permission Target", status: "permission-set", required: true, metadataTypes: ["PermissionSet"], metadataType: "PermissionSet", targetLabel: "Permission set", placeholder: "Select permission set", emptyHint: "No permission set found.", help: "This agent needs the permission set before making a recommendation." };
  }
  // Org-scoped agents whose prompt needs a NAMED entity the user must type into the
  // Instruction box (no metadata picker fits). Surface a concrete hint so the run
  // form prompts for it instead of the misleading "no target is needed".
  // Deactivation-impact needs a specific org USER — give it a live user picker
  // (value = the 18-char User Id, like the class picker) rather than free text.
  if (/deactivat/.test(text)) {
    return {
      kind: "user",
      title: "User Target",
      status: "required",
      required: true,
      metadataType: "User",
      targetLabel: "User",
      placeholder: "Select a user",
      emptyHint: "No active users found (the org token may be unavailable).",
      help: "Pick the Salesforce user to analyze — its Id is passed to the agent.",
    };
  }
  // Other org-scoped agents that still need a NAMED value typed into the Instruction.
  const inputHint =
    /pricebook/.test(text)
      ? "Describe the pricebook to build — name, products, and prices."
      : /standardi[sz]/.test(text)
      ? "Name the profile(s) to standardize and the target standard to align them to."
      : "";
  if (inputHint) {
    return { ...ORG_TARGET_POLICY, needsInput: true, inputHint, help: inputHint };
  }

  return ORG_TARGET_POLICY;
}
